// frontend/src/pages/MobilePick.js
import React, { useState, useEffect } from 'react';
import { api } from '../App';
import ChannelStatusBar from '../components/ChannelStatusBar';
import ImageLightbox, { parseImageList } from '../components/ImageLightbox';

const STORES = [
  { key: 'ebay_source', label: 'Euphoric Operations', icon: '🛒', color: '#6c63ff' },
  { key: 'ebay_store2', label: 'TNT Operations',      icon: '🏪', color: '#a78bfa' },
  { key: 'walmart',     label: 'Walmart',             icon: '🟡', color: '#fbbf24' },
  { key: 'amazon',      label: 'Amazon',              icon: '📦', color: '#ff9900' },
  { key: 'ebay_store3', label: '🔥 FBA Firestarters', icon: '🔥', color: '#f97316' },
];

const CHANNEL_ICONS = { ebay_source: '🛒', ebay_store2: '🏪', walmart: '🟡', amazon: '📦', ebay_store3: '🔥', mercari: '🔵', poshmark: '🔴' };
const CHANNEL_PILLS = {
  ebay_source: { label: 'Euphoric', bg: 'rgba(108,99,255,.25)', color: '#a89fff' },
  ebay_store2: { label: 'TNT', bg: 'rgba(96,165,250,.25)', color: '#60a5fa' },
  walmart:     { label: 'Walmart', bg: 'rgba(251,191,36,.2)', color: '#fbbf24' },
  amazon:      { label: 'Amazon', bg: 'rgba(255,153,0,.2)', color: '#ff9900' },
  ebay_store3: { label: 'Firestarters', bg: 'rgba(249,115,22,.2)', color: '#f97316' },
  poshmark:    { label: 'Poshmark', bg: 'rgba(150,150,150,.2)', color: '#999' },
  mercari:     { label: 'Mercari', bg: 'rgba(150,150,150,.2)', color: '#999' },
};
function ChannelPill({ channel }) {
  const p = CHANNEL_PILLS[channel] || { label: channel, bg: 'rgba(150,150,150,.2)', color: '#999' };
  return <span style={{ background:p.bg, color:p.color, padding:'2px 8px', borderRadius:6, fontSize:10, fontWeight:700, whiteSpace:'nowrap' }}>{p.label}</span>;
}
function StockBadge({ qty }) {
  if (qty == null) return null;
  const isZero = qty === 0;
  return <span style={{ fontSize:11, color: isZero ? '#ef4444' : '#f0a500', fontWeight: isZero ? 700 : 400 }}>{isZero ? '⚠️ Stock: 0' : `📦 Stock: ${qty} remaining`}</span>;
}
function isUrgent(d) { return d && new Date(d) <= new Date(Date.now() + 86400000); }

// A real shelf bin looks like "C53", "WS", "B26" — NOT blank, "UNKNOWN", or a bare
// eBay/UPC number (which happens when a product is auto-created from an order and
// hasn't had a location assigned yet). Returns the clean bin, or null if missing.
function realBin(loc) {
  const s = String(loc || '').trim();
  if (!s || s.toUpperCase() === 'UNKNOWN') return null;
  if (/^\d{5,}$/.test(s)) return null;
  return s;
}
// Loud, unmissable bin tag — shows the bin, or a red "⚠ NO BIN" when none is set.
function BinTag({ loc, size = 'sm' }) {
  const bin = realBin(loc);
  const big = size === 'lg';
  if (!bin) return (
    <span style={{ background:'#ef4444', color:'#fff', padding: big?'4px 12px':'3px 8px',
      borderRadius:6, fontSize: big?15:11, fontWeight:900, whiteSpace:'nowrap', fontFamily:'monospace' }}>
      ⚠ NO BIN
    </span>
  );
  return (
    <span style={{ background:'rgba(108,99,255,.25)', color:'#a89fff', padding: big?'4px 12px':'3px 8px',
      borderRadius:6, fontSize: big?18:11, fontWeight:800, whiteSpace:'nowrap', fontFamily:'monospace' }}>
      {bin}
    </span>
  );
}
// Big, obvious quantity-to-pick badge. Multiples turn high-contrast amber so the
// picker can't miss that more than one unit is needed.
function QtyBadge({ qty }) {
  const n = qty || 1;
  const multi = n > 1;
  return (
    <span style={{ display:'inline-flex', alignItems:'baseline', gap:4,
      background: multi ? '#fbbf24' : 'rgba(108,99,255,.22)',
      color: multi ? '#1a1d2e' : '#cfc9ff',
      padding:'4px 14px', borderRadius:10, fontWeight:900, lineHeight:1,
      boxShadow: multi ? '0 0 0 2px rgba(251,191,36,.45)' : 'none' }}>
      <span style={{ fontSize:12, fontWeight:700, opacity:.75 }}>QTY</span>
      <span style={{ fontSize:26 }}>{n}</span>
    </span>
  );
}

// ── Sync Screen ───────────────────────────────────────────────────────────────
function SyncScreen({ storeStatus, onSync, onSkip, syncing }) {
  const allDone  = Object.values(storeStatus).every(s => s === 'done' || s === 'error');
  const anyError = Object.values(storeStatus).some(s => s === 'error');
  const hasStarted = Object.values(storeStatus).some(s => s !== 'idle');

  return (
    <div style={{ background:'#0f1117', minHeight:'100vh', display:'flex', flexDirection:'column',
      fontFamily:'system-ui,-apple-system,sans-serif', color:'#fff' }}>

      {/* Header */}
      <div style={{ background:'#1a1d2e', borderBottom:'2px solid #6c63ff', padding:'20px 20px 16px' }}>
        <div style={{ fontSize:22, fontWeight:900 }}>📋 Pick List</div>
        <div style={{ fontSize:13, color:'#666', marginTop:2 }}>
          {new Date().toLocaleDateString('en-US',{weekday:'long',month:'short',day:'numeric'})}
        </div>
      </div>

      {/* Store list */}
      <div style={{ flex:1, padding:'24px 20px' }}>
        <div style={{ fontSize:14, color:'#888', marginBottom:16, fontWeight:600, letterSpacing:.5 }}>
          STORE CONNECTION STATUS
        </div>

        {STORES.map(store => {
          const status = storeStatus[store.key];
          return (
            <div key={store.key} style={{ background:'#1a1d2e', border:'1px solid',
              borderColor: status === 'done' ? 'rgba(74,222,128,.3)' : status === 'error' ? 'rgba(239,68,68,.3)' : '#2a2d3e',
              borderRadius:14, padding:'16px 18px', marginBottom:12,
              display:'flex', alignItems:'center', gap:14,
              transition:'all .3s' }}>

              {/* Store icon */}
              <div style={{ width:44, height:44, borderRadius:12, background:'rgba(108,99,255,.15)',
                display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 }}>
                {store.icon}
              </div>

              {/* Store info */}
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:15, fontWeight:700 }}>{store.label}</div>
                <div style={{ fontSize:12, color:'#666', marginTop:2 }}>
                  {status === 'idle'    && 'Waiting…'}
                  {status === 'syncing' && <span style={{ color:'#fbbf24' }}>Connecting & fetching orders…</span>}
                  {status === 'done'    && <span style={{ color:'#4ade80' }}>✓ Orders synced successfully</span>}
                  {status === 'error'   && <span style={{ color:'#ef4444' }}>✗ Connection failed — check token</span>}
                </div>
              </div>

              {/* Status indicator */}
              <div style={{ flexShrink:0, width:36, height:36, display:'flex', alignItems:'center', justifyContent:'center' }}>
                {status === 'idle' && (
                  <div style={{ width:10, height:10, borderRadius:'50%', background:'#2a2d3e', border:'2px solid #444' }} />
                )}
                {status === 'syncing' && (
                  <div style={{ width:24, height:24, border:'3px solid #fbbf24', borderTopColor:'transparent',
                    borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
                )}
                {status === 'done' && (
                  <div style={{ width:32, height:32, borderRadius:'50%', background:'rgba(74,222,128,.2)',
                    border:'2px solid #4ade80', display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize:16, color:'#4ade80' }}>✓</div>
                )}
                {status === 'error' && (
                  <div style={{ width:32, height:32, borderRadius:'50%', background:'rgba(239,68,68,.2)',
                    border:'2px solid #ef4444', display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize:16, color:'#ef4444' }}>✗</div>
                )}
              </div>
            </div>
          );
        })}

        {/* Error message */}
        {anyError && allDone && (
          <div style={{ background:'rgba(239,68,68,.08)', border:'1px solid rgba(239,68,68,.25)',
            borderRadius:12, padding:'12px 16px', marginTop:8 }}>
            <div style={{ fontSize:13, fontWeight:700, color:'#ef4444', marginBottom:4 }}>⚠️ One or more stores failed to connect</div>
            <div style={{ fontSize:12, color:'#888' }}>
              Orders from offline stores won't appear on the pick list. Check your API tokens in the dashboard and try again.
            </div>
          </div>
        )}
      </div>

      {/* Buttons */}
      <div style={{ padding:'16px 20px 40px', display:'flex', flexDirection:'column', gap:10 }}>
        {!hasStarted ? (
          <>
            <button onClick={onSync} disabled={syncing}
              style={{ width:'100%', padding:'18px', background:'linear-gradient(135deg,#6c63ff,#5b54e8)',
                border:'none', borderRadius:14, color:'#fff', fontSize:18, fontWeight:800,
                cursor:'pointer', boxShadow:'0 4px 20px rgba(108,99,255,.4)' }}>
              🔄 Sync All Stores
            </button>
            <button onClick={onSkip}
              style={{ width:'100%', padding:'14px', background:'transparent',
                border:'1px solid #2a2d3e', borderRadius:14, color:'#555', fontSize:14,
                cursor:'pointer' }}>
              Skip — Use Last Sync
            </button>
          </>
        ) : allDone ? (
          <button onClick={onSkip}
            style={{ width:'100%', padding:'18px', background: anyError ? '#2a2d3e' : 'linear-gradient(135deg,#4ade80,#22c55e)',
              border:'none', borderRadius:14, color: anyError ? '#fff' : '#000', fontSize:18, fontWeight:800,
              cursor:'pointer', boxShadow: anyError ? 'none' : '0 4px 20px rgba(74,222,128,.3)' }}>
            {anyError ? '⚠️ Continue Anyway →' : '✅ View Pick List →'}
          </button>
        ) : (
          <div style={{ textAlign:'center', color:'#555', fontSize:14, padding:16 }}>
            Syncing stores…
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── List View ─────────────────────────────────────────────────────────────────
function ListView({ bins, checked, toggle, allDone, onImageTap }) {
  const allItems = bins.flatMap(bin => bin.items.map(item => ({ ...item, binLocation: bin.location })));
  return (
    <div style={{ padding:'12px', paddingBottom:20 }}>
      {allDone && allItems.length > 0 && (
        <div style={{ textAlign:'center', padding:'14px', marginBottom:12,
          background:'rgba(74,222,128,.08)', border:'1px solid rgba(74,222,128,.25)', borderRadius:14 }}>
          <div style={{ fontSize:32 }}>🎉</div>
          <div style={{ fontSize:16, fontWeight:800, color:'#4ade80', marginTop:4 }}>All Picked!</div>
          <div style={{ fontSize:12, color:'#555', marginTop:2 }}>Tap any item to undo</div>
        </div>
      )}
      {allItems.map(item => {
        const done = !!checked[item.id];
        return (
          <div key={item.id} onClick={() => toggle(item.id)}
            style={{ display:'flex', gap:12, alignItems:'center', padding:'12px',
              background: done ? 'rgba(74,222,128,.06)' : '#1a1d2e',
              border: done ? '1px solid rgba(74,222,128,.2)' : '1px solid #2a2d3e',
              borderRadius:12, marginBottom:8, cursor:'pointer',
              opacity: done ? .6 : 1, transition:'all .2s' }}>
            <div style={{ flexShrink:0, width:28, height:28, borderRadius:8,
              background: done ? '#4ade80' : 'transparent',
              border: done ? '2px solid #4ade80' : '2px solid #444',
              display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, color:'#000' }}>
              {done ? '✓' : ''}
            </div>
            {(() => { const imgs = parseImageList(item.images); const src = imgs[0]; return src ? (
              <div style={{ position:'relative', flexShrink:0, cursor:'zoom-in' }}
                onClick={e => { e.stopPropagation(); onImageTap(imgs); }}>
                <img src={src} alt="" style={{ width:44,height:44,objectFit:'cover',borderRadius:8,border:'1px solid #2a2d3e', display:'block' }} />
                <div style={{ position:'absolute', bottom:-3, right:-3, background:'rgba(0,0,0,.8)', color:'#fff',
                  fontSize:9, padding:'1px 3px', borderRadius:6, lineHeight:1, border:'1px solid #2a2d3e' }}>🔍</div>
              </div>
            ) : (
              <div style={{ width:44,height:44,borderRadius:8,background:'#2a2d3e',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18 }}>📦</div>
            ); })()}
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:14, fontWeight:600, color: done ? '#4ade80' : '#f0f0f0',
                wordBreak:'break-word', lineHeight:1.3,
                textDecoration: done ? 'line-through' : 'none' }}>
                {item.title || item.sku}
              </div>
              {item.variation && (
                <div style={{ fontSize:12, color:'#fbbf24', fontWeight:600, marginTop:2 }}>
                  {item.variation}
                </div>
              )}
              <div style={{ display:'flex', gap:8, marginTop:3, flexWrap:'wrap' }}>
                <span style={{ fontSize:11, fontFamily:'monospace', color:'#6c63ff' }}>{item.sku}</span>
                <QtyBadge qty={item.quantity_sold} />
                {item.combinable_count > 0 && (
                  <span title="Same buyer has other open orders"
                    style={{ fontSize:10, padding:'1px 6px', borderRadius:4, fontWeight:700, background:'rgba(251,191,36,.22)', color:'#fbbf24' }}>
                    Combine +{item.combinable_count}
                  </span>
                )}
                <StockBadge qty={item.stock_qty} />
                {item.ship_by_date && (
                  <span style={{ fontSize:11, color: isUrgent(item.ship_by_date) ? '#ef4444' : '#666' }}>
                    {isUrgent(item.ship_by_date) ? '🚨 ' : ''}Ship {new Date(item.ship_by_date).toLocaleDateString('en-US',{month:'short',day:'numeric'})}
                  </span>
                )}
              </div>
            </div>
            <div style={{ flexShrink:0, display:'flex', flexDirection:'column', alignItems:'center', gap:2 }}>
              <BinTag loc={item.binLocation} size="lg" />
              <ChannelPill channel={item.channel} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Bin View ──────────────────────────────────────────────────────────────────
function BinView({ bins, checked, toggle, currentBinIdx, setCurrentBinIdx, onImageTap }) {
  const currentBin   = bins[currentBinIdx];
  const pendingItems = currentBin?.items.filter(i => !checked[i.id]) || [];
  const doneItems    = currentBin?.items.filter(i =>  checked[i.id]) || [];

  return (
    <div style={{ paddingBottom:80 }}>
      <div style={{ display:'flex', gap:6, padding:'10px 12px', overflowX:'auto',
        background:'#13151f', borderBottom:'1px solid #2a2d3e', scrollbarWidth:'none' }}>
        {bins.map((bin, idx) => {
          const binDone = bin.items.filter(i => checked[i.id]).length;
          const allDone = binDone === bin.items.length;
          return (
            <button key={bin.location} onClick={() => setCurrentBinIdx(idx)}
              style={{ flexShrink:0, padding:'7px 14px', borderRadius:20, border:'none', cursor:'pointer',
                fontSize:13, fontWeight:700,
                background: idx === currentBinIdx ? '#6c63ff' : allDone ? 'rgba(74,222,128,.15)' : !realBin(bin.location) ? '#ef4444' : '#2a2d3e',
                color: idx === currentBinIdx ? '#fff' : allDone ? '#4ade80' : !realBin(bin.location) ? '#fff' : '#999' }}>
              {realBin(bin.location) || '⚠ NO BIN'}
              <span style={{ marginLeft:5, fontSize:11, opacity:.8 }}>
                {allDone ? '✓' : `${binDone}/${bin.items.length}`}
              </span>
            </button>
          );
        })}
      </div>

      {currentBin && (
        <div style={{ padding:'12px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <div style={{ fontSize:24, fontWeight:900, color: realBin(currentBin.location) ? '#f0f0f0' : '#ef4444' }}>
              📦 {realBin(currentBin.location) || '⚠ NO BIN — location not set'}
            </div>
            <div style={{ fontSize:13, color:'#666', background:'#1a1d2e', padding:'4px 12px', borderRadius:20 }}>
              {pendingItems.length} order{pendingItems.length !== 1 ? 's' : ''} to pick
            </div>
          </div>

          {pendingItems.length === 0 ? (
            <div style={{ textAlign:'center', padding:'30px', background:'rgba(74,222,128,.05)',
              border:'1px solid rgba(74,222,128,.2)', borderRadius:16, marginBottom:12 }}>
              <div style={{ fontSize:36 }}>✅</div>
              <div style={{ color:'#4ade80', fontWeight:700, marginTop:8 }}>Bin Complete!</div>
            </div>
          ) : pendingItems.map(item => (
            <div key={item.id} style={{ background:'#1a1d2e', border:'1px solid #2a2d3e', borderRadius:16, padding:'14px', marginBottom:10 }}>
              <div style={{ display:'flex', gap:10, marginBottom:8, alignItems:'flex-start' }}>
                {(() => { const imgs = parseImageList(item.images); const src = imgs[0]; return src ? (
                  <div style={{ position:'relative', flexShrink:0, cursor:'zoom-in' }}
                    onClick={e => { e.stopPropagation(); onImageTap(imgs); }}>
                    <img src={src} alt="" style={{ width:56,height:56,objectFit:'cover',borderRadius:8,border:'1px solid #2a2d3e', display:'block' }} />
                    <div style={{ position:'absolute', bottom:-3, right:-3, background:'rgba(0,0,0,.8)', color:'#fff',
                      fontSize:10, padding:'1px 4px', borderRadius:6, lineHeight:1, border:'1px solid #2a2d3e' }}>🔍</div>
                  </div>
                ) : null; })()}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:2 }}>
                    <span style={{ fontFamily:'monospace', fontSize:12, color:'#6c63ff', fontWeight:700 }}>{item.sku}</span>
                    <ChannelPill channel={item.channel} />
                  </div>
                  <div style={{ fontSize:15, fontWeight:700, color:'#f0f0f0', lineHeight:1.3 }}>
                    {item.title || '—'}
                  </div>
                  {item.variation && (
                    <div style={{ fontSize:13, color:'#fbbf24', fontWeight:600, marginTop:4 }}>
                      {item.variation}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12, alignItems:'center' }}>
                <QtyBadge qty={item.quantity_sold} />
                {item.combinable_count > 0 && (
                  <span title="Same buyer has other open orders"
                    style={{ background:'rgba(251,191,36,.25)', color:'#fbbf24',
                      padding:'4px 12px', borderRadius:8, fontSize:13, fontWeight:800 }}>
                    Combine +{item.combinable_count}
                  </span>
                )}
                {item.stock_qty != null && (
                  <span style={{ background: item.stock_qty === 0 ? 'rgba(239,68,68,.15)' : 'rgba(251,191,36,.1)',
                    color: item.stock_qty === 0 ? '#ef4444' : '#f0a500', padding:'4px 10px', borderRadius:8, fontSize:12, fontWeight: item.stock_qty === 0 ? 700 : 400 }}>
                    {item.stock_qty === 0 ? '⚠️ Stock: 0' : `📦 Stock: ${item.stock_qty}`}
                  </span>
                )}
                {item.ship_by_date && (
                  <span style={{ background: isUrgent(item.ship_by_date) ? 'rgba(239,68,68,.2)' : 'rgba(255,255,255,.06)',
                    color: isUrgent(item.ship_by_date) ? '#ef4444' : '#888', padding:'4px 10px', borderRadius:8, fontSize:12 }}>
                    {isUrgent(item.ship_by_date) ? '🚨 ' : ''}Ship {new Date(item.ship_by_date).toLocaleDateString('en-US',{month:'short',day:'numeric'})}
                  </span>
                )}
              </div>
              <div style={{ fontSize:11, color:'#555', marginBottom:12, fontFamily:'monospace' }}>
                {item.buyer_name} · #{item.platform_order_id}
              </div>
              <button onClick={() => toggle(item.id)}
                style={{ width:'100%', padding:'16px', background:'linear-gradient(135deg,#6c63ff,#5b54e8)',
                  border:'none', borderRadius:12, color:'#fff', fontSize:18, fontWeight:800,
                  cursor:'pointer', boxShadow:'0 4px 16px rgba(108,99,255,.4)' }}>
                ✓ &nbsp; PICKED
              </button>
            </div>
          ))}

          {doneItems.length > 0 && (
            <div style={{ marginTop:12 }}>
              <div style={{ fontSize:12, color:'#4ade80', fontWeight:700, marginBottom:6 }}>✅ DONE ({doneItems.length})</div>
              {doneItems.map(item => (
                <div key={item.id} onClick={() => toggle(item.id)}
                  style={{ background:'rgba(74,222,128,.04)', border:'1px solid rgba(74,222,128,.15)',
                    borderRadius:10, padding:'10px 14px', marginBottom:6,
                    display:'flex', justifyContent:'space-between', alignItems:'center', cursor:'pointer' }}>
                  <div style={{ minWidth:0, flex:1 }}>
                    <div style={{ fontSize:11, fontFamily:'monospace', color:'#4ade80' }}>{item.sku}</div>
                    <div style={{ fontSize:12, color:'#555', textDecoration:'line-through' }}>{item.title?.substring(0,40)}…</div>
                  </div>
                  <span style={{ fontSize:11, color:'#555', marginLeft:8 }}>Undo</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={{ position:'fixed', bottom:0, left:0, right:0, background:'#13151f',
        borderTop:'1px solid #2a2d3e', padding:'10px 16px', display:'flex', gap:10 }}>
        <button disabled={currentBinIdx===0} onClick={() => setCurrentBinIdx(i=>i-1)}
          style={{ flex:1, padding:'13px', borderRadius:12, border:'none', fontSize:15, fontWeight:700, cursor:'pointer',
            background: currentBinIdx===0 ? '#1a1d2e' : '#2a2d3e', color: currentBinIdx===0 ? '#333' : '#aaa' }}>
          ← Prev
        </button>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'0 12px', color:'#555', fontSize:13 }}>
          {currentBinIdx+1}/{bins.length}
        </div>
        <button disabled={currentBinIdx===bins.length-1} onClick={() => setCurrentBinIdx(i=>i+1)}
          style={{ flex:1, padding:'13px', borderRadius:12, border:'none', fontSize:15, fontWeight:700, cursor:'pointer',
            background: currentBinIdx===bins.length-1 ? '#1a1d2e' : '#6c63ff',
            color: currentBinIdx===bins.length-1 ? '#333' : '#fff' }}>
          Next →
        </button>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function MobilePick() {
  const [data, setData]         = useState(null);
  const [checked, setChecked]   = useState({});
  const [loading, setLoading]   = useState(false);
  const [view, setView]         = useState('list');
  const [lightbox, setLightbox] = useState(null); // full-size image viewer (array of urls)
  const [currentBinIdx, setCurrentBinIdx] = useState(0);
  const [syncScreen, setSyncScreen] = useState(true);
  const [syncing, setSyncing]   = useState(false);
  const [storeStatus, setStoreStatus] = useState({
    ebay_source: 'idle',
    ebay_store2: 'idle',
    walmart:     'idle',
    amazon:      'idle',
    ebay_store3: 'idle',
  });

  function load() {
    setLoading(true);
    api.get('/picklist').then(r => {
      setData(r.data);
      const alreadyPicked = {};
      r.data.bins?.forEach(bin => bin.items.forEach(item => {
        if (item.order_status === 'picked') alreadyPicked[item.id] = true;
      }));
      setChecked(alreadyPicked);
      setLoading(false);
    });
  }

  async function syncAllStores() {
    setSyncing(true);
    setStoreStatus({ ebay_source: 'syncing', ebay_store2: 'syncing', walmart: 'syncing', amazon: 'syncing', ebay_store3: 'syncing' });
    try {
      await api.post('/sync/orders');
      setStoreStatus({ ebay_source: 'done', ebay_store2: 'done', walmart: 'done', amazon: 'done', ebay_store3: 'done' });
    } catch(e) {
      setStoreStatus({ ebay_source: 'error', ebay_store2: 'error', walmart: 'error', amazon: 'error', ebay_store3: 'error' });
    }
    setSyncing(false);
    // Auto-advance to pick list after 1.5s
    setTimeout(() => { setSyncScreen(false); load(); }, 1500);
  }

  function skipToPickList() {
    setSyncScreen(false);
    load();
  }

  function toggle(orderId) {
    const newVal = !checked[orderId];
    setChecked(prev => ({ ...prev, [orderId]: newVal }));
    api.patch(`/orders/${orderId}/status`, { status: newVal ? 'picked' : 'pending' }).catch(() => {});
  }

  // Show sync screen first
  if (syncScreen) {
    return (
      <SyncScreen
        storeStatus={storeStatus}
        syncing={syncing}
        onSync={syncAllStores}
        onSkip={skipToPickList}
      />
    );
  }

  const bins      = data?.bins || [];
  const total     = data?.total_orders || 0;
  const doneCount = Object.values(checked).filter(Boolean).length;
  const allDone   = total > 0 && doneCount === total;

  if (loading) return (
    <div style={{ background:'#0f1117', minHeight:'100vh', display:'flex', flexDirection:'column',
      alignItems:'center', justifyContent:'center', color:'#fff', gap:16 }}>
      <div style={{ fontSize:40 }}>⏳</div>
      <div style={{ fontSize:18, fontWeight:700 }}>Loading pick list…</div>
    </div>
  );

  return (
    <div style={{ background:'#0f1117', minHeight:'100vh', color:'#fff',
      fontFamily:'system-ui,-apple-system,sans-serif' }}>

      {/* Header */}
      <div style={{ background:'#1a1d2e', borderBottom:'2px solid #6c63ff',
        padding:'12px 16px', position:'sticky', top:0, zIndex:20 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
          <div>
            <div style={{ fontSize:18, fontWeight:800 }}>📋 Pick List</div>
            <div style={{ fontSize:11, color:'#888' }}>
              {new Date().toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric'})}
            </div>
          </div>
          <button onClick={() => setSyncScreen(true)}
            style={{ background:'rgba(108,99,255,.25)', border:'1px solid rgba(108,99,255,.4)',
              borderRadius:10, padding:'7px 14px', color:'#a89fff', fontSize:13, cursor:'pointer', fontWeight:600 }}>
            🔄 Sync
          </button>
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
          <div style={{ flex:1, height:7, background:'#2a2d3e', borderRadius:99, overflow:'hidden' }}>
            <div style={{ height:'100%', width:`${total?(doneCount/total)*100:0}%`,
              background: allDone ? '#4ade80' : '#6c63ff', borderRadius:99, transition:'width .4s' }} />
          </div>
          <div style={{ fontSize:13, fontWeight:700, color: allDone?'#4ade80':'#a89fff', minWidth:50, textAlign:'right' }}>
            {doneCount}/{total}
          </div>
        </div>

        <div style={{ display:'flex', gap:6 }}>
          <button onClick={() => setView('list')}
            style={{ flex:1, padding:'8px', borderRadius:10, border:'none', cursor:'pointer', fontSize:13, fontWeight:700,
              background: view==='list' ? '#6c63ff' : '#2a2d3e', color: view==='list' ? '#fff' : '#888' }}>
            📋 All Items
          </button>
          <button onClick={() => setView('bin')}
            style={{ flex:1, padding:'8px', borderRadius:10, border:'none', cursor:'pointer', fontSize:13, fontWeight:700,
              background: view==='bin' ? '#6c63ff' : '#2a2d3e', color: view==='bin' ? '#fff' : '#888' }}>
            📦 By Bin
          </button>
        </div>
      </div>

      {total === 0 ? (
        <div style={{ textAlign:'center', padding:'80px 20px' }}>
          <div style={{ fontSize:80 }}>✅</div>
          <div style={{ fontSize:22, fontWeight:800, marginTop:12 }}>No Pending Orders</div>
        </div>
      ) : view === 'list' ? (
        <ListView bins={bins} checked={checked} toggle={toggle} allDone={allDone} onImageTap={setLightbox} />
      ) : (
        <BinView bins={bins} checked={checked} toggle={toggle}
          currentBinIdx={currentBinIdx} setCurrentBinIdx={setCurrentBinIdx} allDone={allDone} onImageTap={setLightbox} />
      )}
      <div style={{ height: 44 }} />
      <ChannelStatusBar />
      {lightbox && <ImageLightbox images={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}
